import {filterNotNull} from "./ArrayUtil";
import {Opt} from "./Optional";
import {keyFn} from "./FunctionUtil";
import {INeighbourhood} from "../api/Model";
import {asDates, DateRangeOption} from "../components/filter-fields/DateRangeFilter";


export type Filter<T> = ((value: T) => boolean) | undefined;

export function applyFilter<R>(rows: R[], filter: Filter<R>): R[] {
    return filter ? rows.filter(filter) : rows;
}

export function matchesSome<R>(rows: R[], filter: Filter<R>): boolean {
    return filter ? rows.some(filter) : false;
}

export function matchesFilter<R>(value: R, filter: Filter<R>): boolean {
    return filter ? filter(value) : true;
}

export function filterValue<R, V>(
    match: Opt<(singleValue: V) => boolean>,
    getValue: (row: R) => (V[] | V),
): Filter<R> {
    if (match === undefined || match === null) {
        return undefined;
    } else {
        return matchValueOrArray(getValue, match);
    }
}

export function matchIfSelected<V>(filterValue: Opt<V>, notSelectedValue: Opt<V> = undefined): Opt<(singleValue: V) => boolean> {
    if (filterValue === undefined || filterValue === null || filterValue === notSelectedValue) {
        return undefined;
    } else {
        return singleValue => singleValue === filterValue;
    }
}

export function optionalFilter<R, V>(
    filterValue: Opt<V>,
    emptyFilterValue: V,
    filter: (value: NonNullable<V>) => Filter<R>,
): Filter<R> {
    if (filterValue === undefined || filterValue === null || filterValue === emptyFilterValue) {
        return undefined;
    } else {
        return filter(filterValue);
    }
}

export function filterChoice<V extends string | number, R>(filter: Opt<V>, options: Record<V, Filter<R>>): Filter<R> {
    if (filter === undefined || filter === null) {
        return undefined;
    } else {
        return options[filter];
    }
}

export function matchMultiSelect<V extends string | number>(
    filterValues: V[],
): ((singleValue: V) => boolean) | undefined {
    if (filterValues.length <= 0) {
        return undefined;
    } else {
        const filterValueSet = new Set(filterValues);
        return singleValue => filterValueSet.has(singleValue);
    }
}

export function filterNeighbourhoods<R>(
    matchNeighbourhoods: Opt<INeighbourhood[]>,
    getValue: (row: R) => Opt<INeighbourhood[]>,
): Filter<R> {
    return filterValue(
        matchNeighbourhoods && matchMultiSelect(matchNeighbourhoods.map(keyFn)),
        row => getValue(row)?.map(keyFn) ?? [],
    );
}

function matchValueOrArray<R, V>(
    getValue: (row: R) => V | V[],
    match: (singleValue: V) => boolean,
): (row: R) => boolean {
    return row => {
        const rowValue = getValue(row);
        if (Array.isArray(rowValue)) {
            return rowValue.some(match);
        } else {
            return match(rowValue);
        }
    }
}

function normalizeForTextSearch(value: Opt<string>): Opt<string> {
    if (!value) {
        return value;
    } else {
        return value.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    }
}

export function filterTextWithInit<R>(
    query: Opt<string>,
    getTextFilter: () => ((row: R) => (Opt<string> | Opt<string>[])),
): Filter<R> {
    const nQuery = normalizeForTextSearch(query);
    if (!nQuery) {
        return undefined;
    } else {
        const getText = getTextFilter();
        return (row: R) => {
            const rowText = getText(row);
            if (Array.isArray(rowText)) {
                return filterNotNull(rowText.map(normalizeForTextSearch))
                    .some(nValue => nValue.includes(nQuery));
            } else {
                const nRowText = normalizeForTextSearch(rowText);
                return nRowText ? nRowText.includes(nQuery) : false;
            }
        }
    }
}

export function filterText<R>(
    query: Opt<string>,
    getText: (row: R) => (Opt<string> | Opt<string>[]),
): Filter<R> {
    return filterTextWithInit(query, () => getText);
}

export function filterDateRange(dateRange?: Opt<DateRangeOption>): Filter<string> {
    const {from: fromDate, to: toDate} = asDates(dateRange);
    if (!fromDate && !toDate) {
        return undefined;
    } else {
        return date => (
            (!fromDate || fromDate <= date) &&
            (!toDate || date <= toDate)
        )
    }
}

export function andFilter<T>(...filters: Filter<T>[]): Filter<T> {
    const meaningful = filterNotNull(filters)
    return meaningful.length > 0
        ? value => meaningful.reduce((acc, cmp) => acc && cmp(value), true)
        : undefined
}
